import type { GeoJsonProperties } from 'geojson'

import { LineStyle } from '@/domain/protocols/UserGeometry'

// Resolves the Mapbox paint values for a user geometry feature from its stored
// (FlatGeobuf) properties. Ported from the mobile (pilotapp) render rules: the
// same fallback chains, luminance threshold, clamps and defaults so geometries
// render identically across web and mobile.

// Stored property keys (canonical SimpleStyle vocabulary persisted in the .fgb).
export const STROKE_KEY = 'stroke'
export const FILL_KEY = 'fill'
export const MARKER_COLOR_KEY = 'marker-color'
export const STROKE_OPACITY_KEY = 'stroke-opacity'
export const FILL_OPACITY_KEY = 'fill-opacity'
export const STROKE_WIDTH_KEY = 'stroke-width'
export const MARKER_SIZE_KEY = 'marker-size'
export const LINE_STYLE_KEY = 'line-style'
export const HIDDEN_KEY = 'hidden'
export const SHOW_LABEL_KEY = 'showLabel'
export const LABEL_MIN_ZOOM_KEY = 'labelMinZoom'
export const NAME_KEY = 'name'

// Color defaults / luminance rule.
export const DEFAULT_POINT_COLOR = '#FCFCFC'
export const DEFAULT_SHAPE_COLOR = '#000000'
export const DARK_POINT_STROKE_COLOR = '#0D1012'
export const LIGHT_POINT_STROKE_COLOR = '#FCFCFC'
export const POINT_STROKE_LUMINANCE_THRESHOLD = 0.6

// Line width clamp.
export const DEFAULT_LINE_WIDTH = 2
export const MIN_LINE_WIDTH = 1
export const MAX_LINE_WIDTH = 12

// Point size clamp (size is a diameter; the circle radius is half of it).
export const DEFAULT_POINT_SIZE = 16
export const MIN_POINT_SIZE = 4
export const MAX_POINT_SIZE = 64

// Opacity defaults.
export const DEFAULT_STROKE_OPACITY = 0.9
export const DEFAULT_FILL_OPACITY = 0.2

// Label min-zoom clamp + defaults.
export const MIN_LABEL_MIN_ZOOM = 3
export const MAX_LABEL_MIN_ZOOM = 14
export const DEFAULT_LABEL_MIN_ZOOM = 6
export const DEFAULT_POINT_LABEL_MIN_ZOOM = 10

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value))

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

// A color is "present" only if it is a non-empty, non-"none" string. The .fgb
// schema seeds missing colors with an empty string, so '' means "absent".
const resolveColorValue = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim()
  if (!normalized || normalized.toLowerCase() === 'none') {
    return null
  }

  return normalized
}

// Points: marker-color → stroke → fill, default white.
export const resolvePointColor = (properties: GeoJsonProperties): string => {
  return (
    resolveColorValue(properties?.[MARKER_COLOR_KEY]) ??
    resolveColorValue(properties?.[STROKE_KEY]) ??
    resolveColorValue(properties?.[FILL_KEY]) ??
    DEFAULT_POINT_COLOR
  )
}

// Lines / polygons: stroke → fill → marker-color, default black.
export const resolveShapeColor = (properties: GeoJsonProperties): string => {
  return (
    resolveColorValue(properties?.[STROKE_KEY]) ??
    resolveColorValue(properties?.[FILL_KEY]) ??
    resolveColorValue(properties?.[MARKER_COLOR_KEY]) ??
    DEFAULT_SHAPE_COLOR
  )
}

const hexToRgb = (color: string): { r: number; g: number; b: number } | null => {
  const match = color.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (!match) {
    return null
  }

  const hex =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : match[1]

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16)
  }
}

// Picks a contrasting circle outline color from the fill color's perceived
// luminance: bright fills get a dark outline, dark fills get a light one.
export const resolvePointStrokeColor = (pointColor: string): string => {
  const rgb = hexToRgb(pointColor)
  if (!rgb) {
    return DARK_POINT_STROKE_COLOR
  }

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance > POINT_STROKE_LUMINANCE_THRESHOLD ? DARK_POINT_STROKE_COLOR : LIGHT_POINT_STROKE_COLOR
}

export const resolveLineWidth = (properties: GeoJsonProperties): number => {
  const width = toFiniteNumber(properties?.[STROKE_WIDTH_KEY]) ?? DEFAULT_LINE_WIDTH
  return clamp(width, MIN_LINE_WIDTH, MAX_LINE_WIDTH)
}

// Returns the circle radius (half of the clamped point size/diameter).
export const resolvePointRadius = (properties: GeoJsonProperties): number => {
  const size = clamp(
    toFiniteNumber(properties?.[MARKER_SIZE_KEY]) ?? DEFAULT_POINT_SIZE,
    MIN_POINT_SIZE,
    MAX_POINT_SIZE
  )
  return size / 2
}

export const resolveStrokeOpacity = (properties: GeoJsonProperties): number => {
  return clamp(toFiniteNumber(properties?.[STROKE_OPACITY_KEY]) ?? DEFAULT_STROKE_OPACITY, 0, 1)
}

export const resolveFillOpacity = (properties: GeoJsonProperties): number => {
  return clamp(toFiniteNumber(properties?.[FILL_OPACITY_KEY]) ?? DEFAULT_FILL_OPACITY, 0, 1)
}

export const resolveLabelMinZoom = (properties: GeoJsonProperties, isPoint: boolean): number => {
  const fallback = isPoint ? DEFAULT_POINT_LABEL_MIN_ZOOM : DEFAULT_LABEL_MIN_ZOOM
  return clamp(toFiniteNumber(properties?.[LABEL_MIN_ZOOM_KEY]) ?? fallback, MIN_LABEL_MIN_ZOOM, MAX_LABEL_MIN_ZOOM)
}

export const resolveLineStyle = (properties: GeoJsonProperties): LineStyle => {
  const value = properties?.[LINE_STYLE_KEY]
  return value === 'dashed' || value === 'dotted' ? value : 'solid'
}

export const resolveLabelText = (properties: GeoJsonProperties): string => {
  const name = properties?.[NAME_KEY]
  return typeof name === 'string' ? name.trim() : ''
}

export const isHiddenFeature = (properties: GeoJsonProperties): boolean => {
  return properties?.[HIDDEN_KEY] === true
}

export const shouldShowLabel = (properties: GeoJsonProperties): boolean => {
  return properties?.[SHOW_LABEL_KEY] === true
}
